import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@common/contexts/UserContext';
import Breadcrumbs from '@common/components/Breadcrumbs';
import Toolbar from '@common/components/Toolbar';
import EmptyState from '@common/components/EmptyState';
import { DeleteModal } from '@common/components';
import { GridViewIcon, ListViewIcon } from '@common/components/icons';
import {
  searchReports,
  fetchDashboardById,
  updateDashboard,
  bulkDelete,
  getReportFilterOptions,
  convertReportFiltersToApiFormat,
} from '@api/dashboard';
import useAsyncFilterOptions from '@common/hooks/useAsyncFilterOptions';
import {
  isUserAdmin,
  getFilterTypes,
  getFilterValues,
  buildReportFilters,
  isAsyncFilterType,
  REPORT_TYPE,
  STATUS_PUBLISHED,
  STATUS_DRAFT,
  createAdminOnlyCreateButton,
} from '../ReportsTableView/utils';
import { hasRequiredFiltersToConfig } from '../../utils/reportUtils';
import ReportsTableView from '../ReportsTableView';
import SavedReportsTableView from '../SavedReportsTableView/SavedReportsTableView';
import ScheduledReportsTableView from '../ScheduledReportsTableView/ScheduledReportsTableView';
import ReportCard from '../ReportCard';
import { LoadingFallback } from '../ReportsLayout/ReportsLayout';
import * as styles from './styles.module.css';

const TABS = {
  ALL_REPORTS: 'all',
  SAVED_REPORTS: 'saved',
  SCHEDULED_REPORTS: 'scheduled',
};

const emptyFilter = {
  id: Date.now() + Math.random(),
  type: '',
  value: '',
};

const VIEW_MODES = {
  CARD: 'card',
  TABLE: 'table',
};

function ReportsPage({ initialTab }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useUser();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(VIEW_MODES.CARD);
  const [loadingReport, setLoadingReport] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([emptyFilter]);
  // Use initialTab prop if provided, otherwise default to ALL_REPORTS
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab === 'saved') return TABS.SAVED_REPORTS;
    if (initialTab === 'scheduled') return TABS.SCHEDULED_REPORTS;
    return TABS.ALL_REPORTS;
  });

  // Delete modal state for card view
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState('idle');
  const [deleteResults, setDeleteResults] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [selectedReportToDelete, setSelectedReportToDelete] = useState(null);

  // Check if user has admin privileges
  const isAdmin = useMemo(() => isUserAdmin(user), [user]);
  const userContactId = user?.contact_id;

  // Safe tab change handler - prevents non-admin from accessing scheduled tab
  const handleTabChange = useCallback(
    (tab) => {
      if (tab === TABS.SCHEDULED_REPORTS && !isAdmin) {
        return; // Ignore attempt to access scheduled tab for non-admin
      }
      setActiveTab(tab);
    },
    [isAdmin],
  );

  // Redirect non-admin users if they somehow land on scheduled tab (e.g., via direct URL)
  useEffect(() => {
    if (!userLoading && !isAdmin && activeTab === TABS.SCHEDULED_REPORTS) {
      setActiveTab(TABS.ALL_REPORTS);
    }
  }, [userLoading, isAdmin, activeTab]);

  const filterTypes = useMemo(() => getFilterTypes(t), [t]);
  const filterValues = useMemo(() => getFilterValues(t), [t]);

  // Use shared hook for async filter options with caching
  const { loadFilterOptions, handleFilterDropdownOpen } = useAsyncFilterOptions({
    filters,
    fetchFilterOptions: getReportFilterOptions,
    convertFiltersToApiFormat: convertReportFiltersToApiFormat,
    isAsyncFilterType,
    staticFilterValues: filterValues,
  });

  // Handler for creating new report
  const handleCreateReport = useCallback(() => {
    navigate('/create');
  }, [navigate]);

  // Create button configuration (admin-only)
  const createButton = useMemo(
    () => createAdminOnlyCreateButton(t, 'reports.createButton', handleCreateReport, isAdmin),
    [t, handleCreateReport, isAdmin],
  );

  // Fetch reports for card view
  useEffect(() => {
    if (userLoading || !user) {
      setLoading(false);
      setReports([]);
      return;
    }

    if (viewMode === VIEW_MODES.CARD) {
      const fetchReports = async () => {
        try {
          setLoading(true);
          const reportFilters = buildReportFilters(filters, isAdmin, true);
          const response = await searchReports({
            pageSize: 100,
            searchTerm,
            filters: reportFilters,
            sortField: 'name',
            sortOrder: 'asc',
          });
          setReports(response.rows || []);
        } catch (err) {
          console.error('Error fetching reports:', err);
          setReports([]);
        } finally {
          setLoading(false);
        }
      };

      fetchReports();
    }
  }, [viewMode, searchTerm, filters, isAdmin, userLoading, user]);

  // Helper function to refresh reports list (preserves current search/filter state)
  const refreshReportsList = useCallback(async () => {
    try {
      const reportFilters = buildReportFilters(filters, isAdmin, true);
      const response = await searchReports({
        pageSize: 100,
        searchTerm,
        filters: reportFilters,
        sortField: 'name',
        sortOrder: 'asc',
      });
      const allReports = (response.rows || []).filter(
        (item) => item.type === REPORT_TYPE || item.type === String(REPORT_TYPE),
      );
      setReports(allReports);
    } catch (err) {
      console.error('Failed to refresh reports list:', err);
    }
  }, [searchTerm, filters, isAdmin]);

  // Handle viewing/generating a report
  const handleGenerate = useCallback(
    async (report) => {
      try {
        setLoadingReport(true);
        const result = await fetchDashboardById(report.id);
        const reportData = result.status === 'success' && result.data ? result.data : report;

        if (hasRequiredFiltersToConfig(reportData)) {
          navigate(`/${report.id}/configure`);
        } else {
          // No filters needed, go directly to VIEW
          navigate(`/${report.id}/view`, {
            state: {
              filters: {
                entities: {
                  hosts: [],
                  metrics: [],
                  business_services: [],
                  host_groups: [],
                },
              },
            },
          });
        }
      } catch (err) {
        console.error('Error loading report:', err);
        navigate(`/${report.id}/configure`);
      } finally {
        setLoadingReport(false);
      }
    },
    [navigate],
  );

  const handleEditReport = useCallback(
    (report) => {
      navigate(`/${report.id}/edit`);
    },
    [navigate],
  );

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handlePublishReport = useCallback(
    async (report) => {
      try {
        const result = await updateDashboard(report.id, {
          status: STATUS_PUBLISHED,
        });
        if (result.status === 'success') {
          await refreshReportsList();
        }
      } catch (err) {
        console.error('Failed to publish report:', err);
      }
    },
    [refreshReportsList],
  );

  const handleUnpublishReport = useCallback(
    async (report) => {
      try {
        const result = await updateDashboard(report.id, {
          status: STATUS_DRAFT,
        });
        if (result.status === 'success') {
          await refreshReportsList();
        }
      } catch (err) {
        console.error('Failed to unpublish report:', err);
      }
    },
    [refreshReportsList],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedReportToDelete(null);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
    setOpenAccordion(null);
  }, []);

  const handleDeleteSubmit = useCallback(async () => {
    if (!selectedReportToDelete) return;

    setDeleteStatus('loading');
    setDeleteError(null);

    try {
      await bulkDelete({ ids: [selectedReportToDelete.id] });
      setDeleteResults({
        success: [{ name: selectedReportToDelete.name }],
        failed: [],
      });
      setDeleteStatus('results');
      await refreshReportsList();
    } catch (error) {
      setDeleteStatus('error');
      setDeleteError(error.message || t('reports.deleteConfirm.errorTitle'));
    }
  }, [selectedReportToDelete, refreshReportsList, t]);

  const handleDeleteReport = useCallback((report) => {
    setSelectedReportToDelete(report);
    setDeleteModalOpen(true);
    setDeleteStatus('idle');
    setDeleteResults(null);
    setDeleteError(null);
  }, []);

  // Loading state for report navigation
  if (loadingReport) {
    return <LoadingFallback />;
  }

  return (
    <div className={styles.container}>
      <Breadcrumbs items={[{ label: t('reports.pageTitle') }]} />
      {/* Page Header with View Toggle */}
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>{t('reports.pageTitle')}</h1>
        {activeTab === TABS.ALL_REPORTS && (
          <div className={styles.viewToggle}>
            <button
              type="button"
              className={viewMode === VIEW_MODES.CARD ? styles.viewButtonActive : styles.viewButton}
              onClick={() => handleViewModeChange(VIEW_MODES.CARD)}
              title={t('reports.viewMode.card')}
              aria-label={t('reports.viewMode.card')}
            >
              <GridViewIcon />
            </button>
            <button
              type="button"
              className={viewMode === VIEW_MODES.TABLE ? styles.viewButtonActive : styles.viewButton}
              onClick={() => handleViewModeChange(VIEW_MODES.TABLE)}
              title={t('reports.viewMode.table')}
              aria-label={t('reports.viewMode.table')}
            >
              <ListViewIcon />
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          <button
            type="button"
            className={activeTab === TABS.ALL_REPORTS ? styles.tabActive : styles.tab}
            onClick={() => handleTabChange(TABS.ALL_REPORTS)}
          >
            {t('reports.tabs.allReports')}
          </button>
          <button
            type="button"
            className={activeTab === TABS.SAVED_REPORTS ? styles.tabActive : styles.tab}
            onClick={() => handleTabChange(TABS.SAVED_REPORTS)}
          >
            {t('reports.tabs.savedReports')}
          </button>
          {/* Scheduled Reports tab - Admin only */}
          {isAdmin && (
            <button
              type="button"
              className={activeTab === TABS.SCHEDULED_REPORTS ? styles.tabActive : styles.tab}
              onClick={() => handleTabChange(TABS.SCHEDULED_REPORTS)}
            >
              {t('reports.tabs.scheduledReports')}
            </button>
          )}
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === TABS.ALL_REPORTS ? (
        <>
          {/* Unified Toolbar with search and filters - shared between card and table views */}
          <div className={styles.toolbarWrapper}>
            <Toolbar
              onSearchChange={setSearchTerm}
              onFiltersChange={setFilters}
              searchTerm={searchTerm}
              onSearchRemove={() => setSearchTerm('')}
              filters={filters}
              filterTypes={filterTypes}
              filterValues={filterValues}
              loadFilterOptions={loadFilterOptions}
              onFilterDropdownOpen={handleFilterDropdownOpen}
              createButton={createButton}
            />
          </div>

          {/* Content Area - switches based on view mode */}
          {viewMode === VIEW_MODES.TABLE ? (
            <ReportsTableView
              hideHeader
              hideToolbar
              onViewReport={handleGenerate}
              onEditReport={handleEditReport}
              onCreateReport={handleCreateReport}
              isAdmin={isAdmin}
              userLoading={userLoading}
              user={user}
              externalSearchTerm={searchTerm}
              externalFilters={filters}
              onSearchChange={setSearchTerm}
              onFiltersChange={setFilters}
            />
          ) : (
            <div className={styles.content}>
              {loading && <div className={styles.loading}>{t('common.loading')}</div>}

              {!loading && reports.length === 0 && <EmptyState translationKey="reports.noReports" />}

              {!loading && reports.length > 0 && (
                <div className={styles.grid}>
                  {reports.map((report) => (
                    <ReportCard
                      key={report.id}
                      report={report}
                      onGenerate={handleGenerate}
                      onView={handleGenerate}
                      onEdit={handleEditReport}
                      onPublish={handlePublishReport}
                      onUnpublish={handleUnpublishReport}
                      onDelete={handleDeleteReport}
                      isAdmin={isAdmin}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ) : null}

      {/* Saved Reports Tab */}
      {activeTab === TABS.SAVED_REPORTS && (
        <SavedReportsTableView isAdmin={isAdmin} userContactId={userContactId} />
      )}

      {/* Scheduled Reports Tab - Admin only */}
      {activeTab === TABS.SCHEDULED_REPORTS && isAdmin && <ScheduledReportsTableView />}

      {/* Delete Confirmation Modal for Card View */}
      <DeleteModal
        isOpen={deleteModalOpen}
        deleteStatus={deleteStatus}
        deleteResults={deleteResults}
        deleteError={deleteError}
        openAccordion={openAccordion}
        setOpenAccordion={setOpenAccordion}
        selectedCount={selectedReportToDelete ? 1 : 0}
        onSubmit={handleDeleteSubmit}
        onClose={handleCloseDeleteModal}
        translationPrefix="reports.deleteConfirm"
        getItemName={(item) => item.name}
      />
    </div>
  );
}

ReportsPage.propTypes = {
  initialTab: PropTypes.oneOf(['all', 'saved', 'scheduled']),
};

ReportsPage.defaultProps = {
  initialTab: 'all',
};

export default ReportsPage;
